import { supabase, CACHE_TTL_MS, SIMILAR_ARTISTS_COUNT } from '../config/supabase';
import { getSimilarArtistsFromGemini, isDbCacheEnabled } from './gemini';

/**
 * Servizio cache artisti con sistema auto-popolante
 * Logica: Check DB → Se cache valida restituisci, altrimenti chiama Gemini → Salva
 */
class ArtistCacheService {
  
  /**
   * Normalizza nome artista per ricerche case-insensitive
   */
  normalizeName(name) {
    return name.trim().toLowerCase();
  }

  /**
   * Verifica se cache è scaduta (oltre 2 mesi)
   */
  isCacheExpired(lastUpdated) {
    if (!lastUpdated) return true;
    const now = new Date().getTime();
    const updated = new Date(lastUpdated).getTime();
    return (now - updated) > CACHE_TTL_MS;
  }

  /**
   * Log statistiche cache
   */
  async logCacheStats(queryType, cacheHit, responseTime, artistName) {
    try {
      await supabase.from('cache_stats').insert({
        query_type: queryType,
        cache_hit: cacheHit,
        response_time_ms: responseTime,
        artist_searched: artistName,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * STEP 1: Check cache per artista (con supporto Spotify ID per omonimia)
   * Returns: { artist, similarities, fromCache }
   */
  async checkCache(artistName, spotifyId = null) {
    const startTime = Date.now();
    const normalized = this.normalizeName(artistName);

    try {
      let artist = null;
      
      // STRATEGIA 1: Se abbiamo spotify_id, cerca per quello (più preciso, disambigua omonimi)
      if (spotifyId) {
        const { data: spotifyArtist, error: spotifyError } = await supabase
          .from('artists')
          .select('*')
          .eq('spotify_id', spotifyId)
          .single();
        
        if (!spotifyError && spotifyArtist) {
          console.log(`[Supabase Cache] Found by Spotify ID: "${artistName}" (${spotifyId})`);
          artist = spotifyArtist;
        }
      }
      
      // STRATEGIA 2: Se non trovato per spotify_id, cerca per nome normalizzato (fallback)
      if (!artist) {
        const { data: nameArtist, error: nameError } = await supabase
          .from('artists')
          .select('*')
          .eq('normalized_name', normalized)
          .maybeSingle();
        
        if (!nameError && nameArtist) {
          // IMPORTANTE: Se abbiamo spotifyId e l'artista in cache ha uno spotify_id diverso,
          // è un omonimo diverso! Ignora il risultato e tratta come cache miss
          if (spotifyId && nameArtist.spotify_id && nameArtist.spotify_id !== spotifyId) {
            console.log(`[Supabase Cache] Found "${artistName}" but different Spotify ID (cached: ${nameArtist.spotify_id}, searching: ${spotifyId}) - treating as cache miss`);
            await this.logCacheStats('check', false, Date.now() - startTime, artistName);
            return { artist: null, similarities: null, fromCache: false };
          }
          
          artist = nameArtist;
        }
      }

      if (!artist) {
        // Non trovato in cache
        await this.logCacheStats('check', false, Date.now() - startTime, artistName);
        return { artist: null, similarities: null, fromCache: false };
      }

      // Verifica se completo e non scaduto
      if (artist.is_complete && !this.isCacheExpired(artist.last_updated)) {
        // CACHE HIT: prendi similarità
        const { data: similarities, error: simError } = await supabase
          .from('artist_similarities')
          .select(`
            similarity_score,
            similar:artists!similar_artist_id(id, name, normalized_name)
          `)
          .eq('artist_id', artist.id)
          .order('similarity_score', { ascending: false })
          .limit(SIMILAR_ARTISTS_COUNT);

        // IMPORTANTE: Se è marcato completo ma non ha similarità, rifai la ricerca
        if (simError || !similarities || similarities.length === 0) {
          console.log(`[Supabase Cache] Artist "${artistName}" marked complete but no similarities found, resetting...`);
          
          // Reset is_complete per permettere nuova ricerca
          await supabase
            .from('artists')
            .update({ is_complete: false, last_updated: null })
            .eq('id', artist.id);
          
          return { artist: null, similarities: null, fromCache: false };
        }

        if (similarities && similarities.length > 0) {
          // Incrementa contatore ricerche
          await supabase
            .from('artists')
            .update({ search_count: artist.search_count + 1 })
            .eq('id', artist.id);

          await this.logCacheStats('check', true, Date.now() - startTime, artistName);
          
          return { 
            artist, 
            similarities: similarities.map(s => ({
              name: s.similar.name,
              similarity: s.similarity_score * 100 // 0.95 → 95
            })),
            fromCache: true 
          };
        }
      }

      // Cache scaduta o incompleta
      await this.logCacheStats('check', false, Date.now() - startTime, artistName);
      return { artist, similarities: null, fromCache: false };

    } catch (error) {
      return { artist: null, similarities: null, fromCache: false };
    }
  }

  /**
   * STEP 2: Chiama Gemini per ottenere artisti simili
   * Se Gemini fallisce, ritorna null per usare LastFM (ma NON salvare)
   */
  async fetchFromGemini(artistName, spotifyId = null, genres = null) {
    const startTime = Date.now();

    try {
      // Se non abbiamo già spotifyId e genres, cercali su Spotify (per evitare omonimi)
      if (!spotifyId || !genres) {
        try {
          const { searchSpotifyArtist } = await import('./spotify');
          const spotifyResult = await searchSpotifyArtist(artistName);
          if (spotifyResult?.id) {
            spotifyId = spotifyResult.id;
            genres = spotifyResult.genres || [];
            console.log(`[Supabase Cache] Found Spotify data for "${artistName}": ID=${spotifyId}, genres=[${genres.join(', ')}]`);
          }
        } catch (spotifyError) {
          console.log(`[Supabase Cache] Could not get Spotify data for "${artistName}", proceeding without it`);
        }
      } else {
        console.log(`[Supabase Cache] Using provided Spotify data for "${artistName}": ID=${spotifyId}, genres=[${genres?.join(', ') || 'none'}]`);
      }

      // Usa il servizio Gemini esistente con l'ID Spotify e i generi
      const geminiResult = await getSimilarArtistsFromGemini(artistName, spotifyId, genres);
      
      // Gestisci sia il formato array (vecchio) che oggetto { artists, artistInfo } (nuovo)
      let artistsList = null;
      let artistInfo = null;
      
      if (!geminiResult) {
        console.log(`[Supabase Cache] Gemini returned no results for "${artistName}"`);
        return null; // Fallback a LastFM
      }
      
      if (Array.isArray(geminiResult)) {
        // Formato vecchio: array diretto
        artistsList = geminiResult;
      } else if (geminiResult.artists && Array.isArray(geminiResult.artists)) {
        // Formato nuovo: oggetto con artists e artistInfo
        artistsList = geminiResult.artists;
        artistInfo = geminiResult.artistInfo || null;
      } else {
        console.log(`[Supabase Cache] Gemini returned invalid format for "${artistName}"`);
        return null;
      }
      
      if (artistsList.length === 0) {
        console.log(`[Supabase Cache] Gemini returned empty list for "${artistName}"`);
        return null; // Fallback a LastFM
      }

      // Converte dal formato LastFM-compatibile a quello cache, preservando genre e origin
      const artists = artistsList.map(artist => ({
        name: artist.name,
        similarity: parseFloat(artist.match) * 100, // 0.95 -> 95
        genre: artist.genre || null, // Genere per disambiguazione
        origin: artist.origin || null // Paese/città per disambiguazione
      }));

      // Con Prompt V3, accettiamo anche meno di 50 risultati (minimo 5)
      if (artists.length < 5) {
        console.log(`[Supabase Cache] Gemini returned only ${artists.length} results (minimum is 5), using LastFM fallback`);
        return null; // Fallback se troppo pochi risultati
      }

      await this.logCacheStats('gemini', false, Date.now() - startTime, artistName);
      
      console.log(`[Supabase Cache] Gemini returned ${artists.length} meaningful results for "${artistName}"`);
      
      if (artistInfo) {
        console.log(`[Supabase Cache] Gemini also returned bilingual artist info`);
      }
      
      // Restituisci risultati con artistInfo opzionale
      return {
        artists,
        artistInfo
      };

    } catch (error) {
      console.log(`[Supabase Cache] Gemini error for "${artistName}": ${error.message}`);
      return null; // Fallback a LastFM
    }
  }

  /**
   * STEP 3: Salva risultati Gemini in database (transazione)
   */
  async saveToCache(artistName, geminiResults, artistInfo = null, spotifyId = null) {
    const startTime = Date.now();
    const normalized = this.normalizeName(artistName);

    try {
      // 1. Inserisci o aggiorna artista principale
      const artistData = {
        name: artistName,
        normalized_name: normalized,
        spotify_id: spotifyId || null, // Salva Spotify ID per disambiguare omonimi
        is_complete: true,
        search_count: 1, // Prima ricerca
        last_updated: new Date().toISOString()
      };
      
      // Aggiungi artist_info bilingua se fornito
      if (artistInfo) {
        if (typeof artistInfo === 'object' && artistInfo.it && artistInfo.en) {
          // Nuovo formato bilingua
          artistData.artist_info_it = artistInfo.it;
          artistData.artist_info_en = artistInfo.en;
          console.log(`[Supabase Cache] Saving bilingual artist_info for "${artistName}" (IT: ${artistInfo.it.length} chars, EN: ${artistInfo.en.length} chars)`);
        } else if (typeof artistInfo === 'string') {
          // Vecchio formato (retrocompatibilità)
          artistData.artist_info_it = artistInfo;
          artistData.artist_info_en = artistInfo;
          console.log(`[Supabase Cache] Saving artist_info for "${artistName}" (${artistInfo.length} characters)`);
        }
      }
      
      const { data: mainArtist, error: mainError } = await supabase
        .from('artists')
        .upsert(artistData, {
          onConflict: 'normalized_name',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (mainError) throw mainError;

      // Se esiste già, incrementa search_count (e aggiorna artist_info se fornito)
      if (mainArtist.search_count > 1) {
        const updateData = { 
          is_complete: true,
          last_updated: new Date().toISOString(),
          search_count: mainArtist.search_count + 1 
        };
        
        // Aggiorna artist_info bilingua solo se fornito (sovrascrive quello esistente)
        if (artistInfo) {
          if (typeof artistInfo === 'object' && artistInfo.it && artistInfo.en) {
            updateData.artist_info_it = artistInfo.it;
            updateData.artist_info_en = artistInfo.en;
          } else if (typeof artistInfo === 'string') {
            updateData.artist_info_it = artistInfo;
            updateData.artist_info_en = artistInfo;
          }
        }
        
        await supabase
          .from('artists')
          .update(updateData)
          .eq('id', mainArtist.id);
      }

      // 2. Inserisci artisti simili (se non esistono)
      const similarArtistsToInsert = geminiResults.map(r => ({
        name: r.name,
        normalized_name: this.normalizeName(r.name),
        is_complete: false, // Saranno interrogati quando cercati
        search_count: 0
      }));

      // Upsert artisti simili
      const { data: insertedArtists } = await supabase
        .from('artists')
        .upsert(similarArtistsToInsert, {
          onConflict: 'normalized_name',
          ignoreDuplicates: true
        })
        .select('id, normalized_name');

      // 3. Crea mapping nome → id per similarità
      const nameToIdMap = {};
      if (insertedArtists) {
        insertedArtists.forEach(a => {
          nameToIdMap[a.normalized_name] = a.id;
        });
      }

      // Query per ottenere tutti gli ID (inclusi quelli già esistenti)
      const normalizedNames = geminiResults.map(r => this.normalizeName(r.name));
      const { data: allArtists } = await supabase
        .from('artists')
        .select('id, normalized_name')
        .in('normalized_name', normalizedNames);

      if (allArtists) {
        allArtists.forEach(a => {
          nameToIdMap[a.normalized_name] = a.id;
        });
      }

      // 4. Elimina vecchie similarità per questo artista (refresh)
      await supabase
        .from('artist_similarities')
        .delete()
        .eq('artist_id', mainArtist.id);

      // 5. Inserisci nuove similarità
      // Filtra: no duplicati, no self-reference, solo artisti con ID valido
      const seenIds = new Set();
      const similarities = geminiResults
        .filter(r => {
          const normalized = this.normalizeName(r.name);
          const similarId = nameToIdMap[normalized];
          
          // Salta se: nessun ID, è se stesso, già visto
          if (!similarId) return false;
          if (similarId === mainArtist.id) return false; // No self-reference
          if (seenIds.has(similarId)) return false; // No duplicati
          
          seenIds.add(similarId);
          return true;
        })
        .map(r => ({
          artist_id: mainArtist.id,
          similar_artist_id: nameToIdMap[this.normalizeName(r.name)],
          similarity_score: r.similarity / 100 // 95 → 0.95
        }));

      if (similarities.length === 0) {
        throw new Error('No valid similarities to save after filtering');
      }

      // Usa upsert invece di insert per gestire eventuali duplicati residui
      const { error: simError } = await supabase
        .from('artist_similarities')
        .upsert(similarities, {
          onConflict: 'artist_id,similar_artist_id',
          ignoreDuplicates: false
        });

      if (simError) {
        console.log(`[Supabase Cache] Error saving similarities: ${simError.message}`);
        throw simError;
      }

      // IMPORTANTE: Verifica che siano state salvate effettivamente
      const { count: savedCount } = await supabase
        .from('artist_similarities')
        .select('*', { count: 'exact', head: true })
        .eq('artist_id', mainArtist.id);

      console.log(`[Supabase Cache] Saved ${savedCount} similarities for "${artistName}"`);

      // Se non abbiamo salvato abbastanza similarità, non marcare come completo
      if (savedCount < SIMILAR_ARTISTS_COUNT * 0.9) { // Almeno 90% (45 su 50)
        throw new Error(`Only ${savedCount} similarities saved, expected ${SIMILAR_ARTISTS_COUNT}`);
      }

      await this.logCacheStats('save', false, Date.now() - startTime, artistName);

      return { success: true, artistId: mainArtist.id, count: savedCount };

    } catch (error) {
      throw error;
    }
  }

  /**
   * METODO PRINCIPALE: Cerca artisti simili (con cache intelligente)
   */
  async getSimilarArtists(artistName, spotifyId = null, genres = null) {
    const totalStartTime = Date.now();

    try {
      // STEP 1: Check cache (solo se abilitato, con Spotify ID per gestire omonimi)
      if (isDbCacheEnabled()) {
        const cacheResult = await this.checkCache(artistName, spotifyId);

        if (cacheResult.fromCache && cacheResult.similarities) {
          console.log(`[Supabase Cache] Cache hit for "${artistName}" (${Date.now() - totalStartTime}ms)`);
          return {
            artists: cacheResult.similarities,
            fromCache: true,
            source: 'supabase',
            responseTime: Date.now() - totalStartTime
          };
        }
      } else {
        console.log(`[Supabase Cache] DB cache disabled - skipping cache check for "${artistName}"`);
      }

      // STEP 2: Cache miss → Chiama Gemini
      console.log(`[Supabase Cache] Cache miss for "${artistName}", trying Gemini...`);
      const geminiResult = await this.fetchFromGemini(artistName, spotifyId, genres);

      // STEP 3: Se Gemini ha successo, salva in cache (se abilitato)
      // geminiResult ha il formato { artists, artistInfo } dove artists è già normalizzato
      let geminiResults, artistInfo = null;
      
      if (geminiResult && typeof geminiResult === 'object') {
        // Formato da fetchFromGemini: { artists: [...], artistInfo: {...} }
        if (geminiResult.artists && Array.isArray(geminiResult.artists)) {
          geminiResults = geminiResult.artists;
          artistInfo = geminiResult.artistInfo || null;
        }
      }
      
      // Con Prompt V3, accettiamo 5-50 risultati significativi
      if (geminiResults && geminiResults.length >= 5) {
        // fetchFromGemini già ritorna il formato corretto con similarity (number 0-100)
        const normalizedResults = geminiResults.map(artist => ({
          name: artist.name,
          similarity: artist.similarity, // Già in formato 0-100
          mbid: artist.mbid || '',
          url: artist.url || `https://www.last.fm/music/${encodeURIComponent(artist.name)}`
        }));
        
        // Controlla se il salvataggio su DB è abilitato
        if (isDbCacheEnabled()) {
          await this.saveToCache(artistName, normalizedResults, artistInfo, spotifyId);
          console.log(`[Supabase Cache] Saved "${artistName}" to cache with ${normalizedResults.length} results${artistInfo ? ' + artist_info' : ''}${spotifyId ? ` (Spotify ID: ${spotifyId})` : ''} (${Date.now() - totalStartTime}ms)`);
        } else {
          console.log(`[Supabase Cache] DB cache disabled - skipping save for "${artistName}"`);
        }

        return {
          artists: normalizedResults,
          fromCache: false,
          source: 'gemini',
          responseTime: Date.now() - totalStartTime,
          artistInfo: artistInfo // Passa artist_info anche al chiamante
        };
      }

      // STEP 4: Gemini fallito → Usa LastFM ma NON salvare
      console.log(`[Supabase Cache] Using LastFM fallback for "${artistName}" (not saving to cache)`);
      
      // Importa dinamicamente LastFM per evitare dipendenze circolari
      // USA getSimilarArtistsFromLastFM direttamente per NON riprovare Gemini
      const { getSimilarArtistsFromLastFM } = await import('./lastfm');
      const lastfmResults = await getSimilarArtistsFromLastFM(artistName);
      
      // Converte formato LastFM a formato cache
      const artists = lastfmResults.map(artist => ({
        name: artist.name,
        similarity: parseFloat(artist.match) * 100
      }));

      return {
        artists: artists.slice(0, SIMILAR_ARTISTS_COUNT),
        fromCache: false,
        source: 'lastfm',
        responseTime: Date.now() - totalStartTime
      };

    } catch (error) {
      // Fallback finale: prova cache anche se scaduta
      const cacheResult = await this.checkCache(artistName, spotifyId);
      if (cacheResult.artist && cacheResult.similarities && cacheResult.similarities.length > 0) {
        console.log(`[Supabase Cache] Using expired cache as last resort for "${artistName}"`);
        return {
          artists: cacheResult.similarities,
          fromCache: true,
          source: 'supabase-expired',
          expired: true,
          responseTime: Date.now() - totalStartTime
        };
      }

      throw error;
    }
  }

  /**
   * Statistiche cache (per dashboard)
   */
  async getCacheStats() {
    try {
      const { data: stats } = await supabase
        .from('cache_performance')
        .select('*')
        .limit(30);

      const { data: topArtists } = await supabase
        .from('top_searched_artists')
        .select('*')
        .limit(20);

      const { count: totalArtists } = await supabase
        .from('artists')
        .select('*', { count: 'exact', head: true });

      const { count: completeArtists } = await supabase
        .from('artists')
        .select('*', { count: 'exact', head: true })
        .eq('is_complete', true);

      return {
        performance: stats || [],
        topSearched: topArtists || [],
        totalArtists: totalArtists || 0,
        completeArtists: completeArtists || 0,
        cacheCompletionRate: totalArtists > 0 
          ? Math.round((completeArtists / totalArtists) * 100) 
          : 0
      };

    } catch (error) {
      return null;
    }
  }

  /**
   * Forza refresh di un artista (ignora cache)
   */
  async forceRefresh(artistName) {
    // Elimina da cache
    const normalized = this.normalizeName(artistName);
    const { data: artist } = await supabase
      .from('artists')
      .select('id')
      .eq('normalized_name', normalized)
      .single();

    if (artist) {
      await supabase
        .from('artist_similarities')
        .delete()
        .eq('artist_id', artist.id);

      await supabase
        .from('artists')
        .update({ is_complete: false, last_updated: null })
        .eq('id', artist.id);
    }

    // Rilancia ricerca
    return this.getSimilarArtists(artistName);
  }
}

export default new ArtistCacheService();

